import {useLazyQuery} from "@apollo/client";
import {endOfDay, format, subDays} from "date-fns";
import {useCallback} from "react";

import {
  GQL_PRIVATE_STATUSPAGE_PDF_QUERY,
  GQL_PUBLIC_STATUSPAGE_PDF_QUERY,
} from "@/lib/queries/statusPageConsumption.ts";
import {PdfResult} from "@/lib/types/common.ts";
import {openPdfDownload} from "@/lib/utils/pdfUtils.ts";

interface UseStatusPagePdfDownloadOptions {
  mode: "public" | "private";
  slug: string;
  pageId: number | undefined;
  defaultHistoryDateRange: number;
}

export const useStatusPagePdfDownload = ({
  mode,
  slug,
  pageId,
  defaultHistoryDateRange,
}: UseStatusPagePdfDownloadOptions) => {
  const [fetchPublicPdf, {loading: publicPdfLoading}] = useLazyQuery<{publicStatusPagePdf: PdfResult | null}>(
    GQL_PUBLIC_STATUSPAGE_PDF_QUERY,
    {
      fetchPolicy: "no-cache",
      context: {statusPageSlug: slug, skipAuthError: true},
    }
  );

  const [fetchPrivatePdf, {loading: privatePdfLoading}] = useLazyQuery<{privateStatusPagePdf: PdfResult | null}>(
    GQL_PRIVATE_STATUSPAGE_PDF_QUERY,
    {fetchPolicy: "no-cache"}
  );

  const loading = publicPdfLoading || privatePdfLoading;

  const downloadPdf = useCallback(
    (range?: {startDate: string; endDate: string}) => {
      let startDate: string;
      let endDate: string;
      if (range) {
        ({startDate, endDate} = range);
      } else {
        // Fallback range when no explicit range is supplied (e.g. programmatic download).
        const days = defaultHistoryDateRange || 7;
        const now = new Date();
        const end = endOfDay(now);
        const start = subDays(end, days);
        startDate = format(start, "yyyyMMdd");
        endDate = format(end, "yyyyMMdd");
      }

      if (mode === "private" && pageId != null) {
        openPdfDownload(() =>
          fetchPrivatePdf({variables: {id: pageId, startDate, endDate}}).then(({data}) => data?.privateStatusPagePdf)
        );
      } else {
        openPdfDownload(() =>
          fetchPublicPdf({variables: {slug, startDate, endDate}}).then(({data}) => data?.publicStatusPagePdf)
        );
      }
    },
    [defaultHistoryDateRange, mode, pageId, slug, fetchPublicPdf, fetchPrivatePdf]
  );

  return {downloadPdf, loading};
};
